import React, { useEffect } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import { useSession, useTeams, useSteps, useWarRoomWS } from '../hooks'
import { useWSStatus } from '../stores/selectors'
import { useSessionStore } from '../stores/sessionStore'
import { ROLE_PERMISSIONS } from '../stores/sessionStore'
import { SessionHeader } from '../components/session'
import { TeamGrid } from '../components/team'
import { AIAlertPanel } from '../components/alerts'
import { ProgressTimeline } from '../components/shared'
import { WSStatusIndicator } from '../components/shared'
import { WarRoomLoadingScreen } from '../components/shared'
import { MobileQuickActions } from '../components/MobileQuickActions'
import styles from './MasterDashboardPage.module.css'

export function MasterDashboardPage() {
  const { sessionId } = useParams<{ sessionId: string }>()

  const { setSessionContext, clearSessionContext } = useSessionStore()
  const wsStatus = useWSStatus()

  const { data: session, isLoading: sessionLoading } = useSession(sessionId ?? '')
  const { data: teams, isLoading: teamsLoading } = useTeams(sessionId ?? '')
  const { data: steps } = useSteps(sessionId ?? '')

  useWarRoomWS({ sessionId: sessionId ?? '' })

  useEffect(() => {
    if (!sessionId) return
    setSessionContext({
      sessionId,
      role: 'commander',
      permissions: ROLE_PERMISSIONS.commander,
    })
    return () => clearSessionContext()
  }, [sessionId, setSessionContext, clearSessionContext])

  if (!sessionId) return <Navigate to="/war-room" replace />
  if (sessionLoading || teamsLoading) return <WarRoomLoadingScreen />
  if (!session) {
    return (
      <div className={styles.dashboard} data-theme="war-room-dark" data-testid="master-dashboard-page">
        <div style={{ textAlign: 'center', padding: '80px 20px', color: '#8b949e' }}>
          <h2>Session not found</h2>
          <p>The requested War Room session ({sessionId}) does not exist or has been removed.</p>
          <a href="/war-room" style={{ color: '#58a6ff', marginTop: '16px', display: 'inline-block' }}>← Back to War Room list</a>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.dashboard} data-theme="war-room-dark" data-testid="master-dashboard-page">
      <WSStatusIndicator status={wsStatus} className={styles.wsIndicator} />

      <SessionHeader session={session} />

      <div className={styles.main}>
        <div className={styles.teamArea}>
          <TeamGrid sessionId={sessionId} teams={teams ?? []} stepsByTeam={steps?.byTeam ?? {}} />
        </div>
        <div className={styles.alertPanel}>
          <AIAlertPanel sessionId={sessionId} />
        </div>
      </div>

      <div className={styles.timeline}>
        <ProgressTimeline
          session={session}
          teams={teams ?? []}
          steps={steps?.all ?? []}
        />
      </div>

      <MobileQuickActions />
    </div>
  )
}

export default MasterDashboardPage
